import { AccessTime as AccessTimeIcon } from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  IconButton,
  Popover,
} from "@mui/material";
import { StaticTimePicker } from "@mui/x-date-pickers";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import type { Dayjs } from "dayjs";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import { useDynamicFormsTranslate, useRtl } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  getRegisterAttributes,
  KeyboardKeys,
  Log,
  MAIN_TIME_FORMAT,
  parseTimeAtReferenceDate,
  TimeZoneEnum,
  useFormats,
  ValidationHelpers,
  type ICustomField,
  type IStyledAttributes,
} from "@ryde/shared";

import { CustomMuiPickersActionBar } from "../../controls";
import { FieldExplanationIcon } from "./_parts";
import { StyledTimeTemplateWithExplanation } from "./_template-styles";

const TimeEditableStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "time-editable-styled",
})`
  border-width: 0;
  display: flex;
  align-items: center;

  .MuiIconButton-root {
    margin-inline-start: 3px;
    margin-inline-end: 5px;
  }
`;

export const TimeTemplate: FC<ICustomField> = ({
  id,
  name,
  formLabel,
  labelTranslated,
  formAttributes,
  formValidation,
  timeFormat,
  e2eData,
}) => {
  const { t } = useTranslation();
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    translatedText: labelTranslated,
    fallbackText: formLabel,
  });
  const { disabled } = getRegisterAttributes(formAttributes);
  const initialValue = getValues(name as string);
  const initialValueComputed = parseTimeAtReferenceDate({
    timeHHmm: initialValue,
    timezone: TimeZoneEnum.UTC,
  });
  const refValue = useRef<Dayjs | null | undefined>(initialValue);
  const { isAMPM, timeFormat: profileTimeFormat } = useFormats();

  const [timeValueUTC, setTimeValueUTC] = useState<Dayjs | null | undefined>(
    initialValueComputed,
  );

  useEffect(() => {
    if (initialValue !== refValue.current) {
      const initialValueUpdated = parseTimeAtReferenceDate({
        timeHHmm: initialValue,
        timezone: TimeZoneEnum.UTC,
      });

      setTimeValueUTC(initialValueUpdated);
      refValue.current = initialValue;
    }
  }, [initialValue]);

  const { isRtl } = useRtl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const refTimeEditableWrapper = useRef<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;
  const handleClose = useEvent((): void => {
    setAnchorEl(null);
  });
  const handleClick = useEvent(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
  );

  const setFormUnderlyingValue = useEvent((newValueUTC: Dayjs | null) => {
    if (!newValueUTC?.isValid()) {
      Log.debug("Invalid time chosen, resetting control value", newValueUTC);
      checkCleanupNestedValidation(name, errors);
      setTimeValueUTC(null);
      setValue(name, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAnchorEl(null);
      return;
    }

    const timeCorrectedTimezone = newValueUTC.format(MAIN_TIME_FORMAT);
    setValue(name, timeCorrectedTimezone, {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const onBlur = useEvent(() => {
    requestAnimationFrame(() => {
      const active = document.activeElement;

      // prevent run validation if focus is still inside the wrapper (popover or input)
      if (refTimeEditableWrapper.current?.contains(active)) {
        return;
      }

      if (!timeValueUTC?.isValid()) {
        Log.warnAndToast(t("errors.invalidTimeFormat"));
        return;
      }

      setFormUnderlyingValue(timeValueUTC);
    });
  });

  const handleTimeInputChange = useEvent((newValueUTCDayJs: Dayjs | null) => {
    setTimeValueUTC(newValueUTCDayJs);
    setFormUnderlyingValue(newValueUTCDayJs);
  });

  const handlePickerAccept = useEvent((newValueUTCDayJs: Dayjs | null) => {
    setTimeValueUTC(newValueUTCDayJs);
    setFormUnderlyingValue(newValueUTCDayJs);
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    onBlur();
  };

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  // The full-width anchor puts the "?" in the column other fields use; time fields without one keep their layout.
  const hasExplanation = Boolean(
    formAttributes?.tooltipTranslationKey || formAttributes?.helpTopikUrl,
  );

  const timeField = (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange: _onChange, ...restField },
        fieldState: { error },
      }) => (
        <FormControl
          required={requiredComputed}
          data-e2e-template="time-template"
          data-e2e-field-name={name}
          error={!!error}
        >
          <TimeEditableStyled ref={refTimeEditableWrapper}>
            <TimeField
              {...restField}
              data-e2e={e2eData}
              data-e2e-id={`${name}_${id || ""}`}
              label={formLabelTranslated}
              required={requiredComputed}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              onChange={handleTimeInputChange}
              value={timeValueUTC}
              disabled={disabled}
              format={timeFormat ?? profileTimeFormat}
              timezone={TimeZoneEnum.UTC}
              slotProps={{
                textField: {
                  error: !!error,
                },
              }}
            />

            <IconButton onClick={handleClick} size={"small"}>
              <AccessTimeIcon className="dropdown_icon" />
            </IconButton>
            <Popover
              id={popoverId}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: isRtl ? "left" : "right",
              }}
            >
              <StaticTimePicker
                value={timeValueUTC}
                onAccept={handlePickerAccept}
                ampm={isAMPM}
                timezone={TimeZoneEnum.UTC}
                onChange={handleTimeInputChange}
                slots={{
                  actionBar: props => (
                    <CustomMuiPickersActionBar
                      customOnCancel={handleClose}
                      customOnAccept={handleClose}
                      {...props}
                    />
                  ),
                }}
              />
            </Popover>
          </TimeEditableStyled>
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );

  if (!hasExplanation) {
    return timeField;
  }

  return (
    <StyledTimeTemplateWithExplanation>
      {timeField}
      <FieldExplanationIcon formAttributes={formAttributes} />
    </StyledTimeTemplateWithExplanation>
  );
};
